import React from 'react'
import PropTypes from 'prop-types'
import { PlaylistIcon } from 'images'
import { Tonality } from '@dsny/dsny-component-library'
import {
  Check,
  Close,
  FlagFilled,
  FlagUnfilled,
  ArchiveFilled,
  ArchiveUnfilled,
  Playlistadd,
  Download,
  Personadd
} from '@dsny/dsny-component-library/icons'
import { NotificationType } from 'constants/enums'
import { NotificationWrapper } from './Notification.styles'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'

const NotificationView = ({ notification, onRemove, type = 'success' }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const processMessage = (category, message) => {
    switch (category) {
      case NotificationType.addDownloadQueue:
        return `${message} ${t(
          'notification.has-been-added-to-download-queue'
        )}`
      case NotificationType.downloadFinished:
        return `${message} ${t('notification.has-been-downloaded')}`
      case NotificationType.hasError:
        return `${t('notification.has-error')}: ${message}. ${t(
          'notification.please-refresh'
        )}`
      case NotificationType.releaseShared:
        return `${message} ${t('share-release.success-toast-message')}`
      case NotificationType.connectionAccepted:
        return `${message} ${t('invitations.is-now-in-your-contacts')}`
      case NotificationType.flag:
        return `${message} ${t('notification.has-been-flagged')}`
      case NotificationType.unFlag:
        return `${message} ${t('notification.has-been-unFlagged')}`
      case NotificationType.archive:
        return `${message} ${t('notification.has-been-archived')}`
      case NotificationType.unArchive:
        return `${message} ${t('notification.has-been-unArchived')}`
      case NotificationType.settingSaved:
        return `${message} ${t('notification.setting-saved')}`
      case NotificationType.profileUpdated:
        return `${message} ${t('notification.profile-info-updated')}`
      case NotificationType.playlistCreated:
        return `${message} ${t('notification.has-been-created')}`
      case NotificationType.playlistUpdated:
        return `${message} ${t('notification.has-been-updated')}`
      case NotificationType.contactRequestSent:
        return t('notification.email-success')
      case NotificationType.addedToPlaylist:
        const { songTitle, playlistTitle } = message
        return `${songTitle} ${t(
          'notification.has-been-added-to-playlist'
        )} ${playlistTitle}`
      case NotificationType.invitationAlreadyAccepted:
        return `${message} ${t('notification.invitation-already-accepted')}`
      case NotificationType.invitationNotForUser:
        return `${message} ${t('notification.Invitation not for user')}`
      case NotificationType.emailSuccessfullyLinked:
        return `${message} ${t('notification.email-successfully-linked')}`
      case NotificationType.emailSuccessfullyUnlinked:
        return `${message} ${t('notification.email-successfully-unlinked')}`
      case NotificationType.emailLinkingFailed:
        return `${message} ${t('notification.email-linking-failed')}`
      case NotificationType.emailUnlinkingFailed:
        return `${message} ${t('notification.email-unlinking-failed')}`

      default:
        return ''
    }
  }

  const renderIcon = (category) => {
    const commonIconProps = {
      width: 24,
      height: 24
    }

    const iconMap = {
      [NotificationType.addDownloadQueue]: <Download {...commonIconProps} />,
      [NotificationType.downloadFinished]: <Download {...commonIconProps} />,
      [NotificationType.flag]: <FlagFilled {...commonIconProps} />,
      [NotificationType.unFlag]: <FlagUnfilled {...commonIconProps} />,
      [NotificationType.archive]: <ArchiveFilled {...commonIconProps} />,
      [NotificationType.unArchive]: <ArchiveUnfilled {...commonIconProps} />,
      [NotificationType.playlistCreated]: <Playlistadd {...commonIconProps} />,
      [NotificationType.addedToPlaylist]: <Playlistadd {...commonIconProps} />,
      [NotificationType.playlistUpdated]: <Playlistadd {...commonIconProps} />,
      [NotificationType.connectionAccepted]: <Personadd {...commonIconProps} />,
      [NotificationType.releaseShared]: (
        <Check width={24} height={24} color={theme.colors.success50} />
      )
    }
    return iconMap[category] || null
  }

  const renderCloseIcon = () => (
    <button
      type="button"
      className="focus:outline-none p-2 ml-auto"
      onClick={() => onRemove(notification.id)}
    >
      <Close width={24} height={24} />
    </button>
  )

  return (
    <NotificationWrapper $type={notification.notificationType || type}>
      {renderIcon(notification.category)}

      <Tonality.UIText
        textStyle="body"
        color={theme.colors.neutralW40}
        style={{ fontSize: 14, lineHeight: '20px', marginLeft: 18 }}
      >
        {processMessage(notification.category, notification.message)}
      </Tonality.UIText>

      {renderCloseIcon()}
    </NotificationWrapper>
  )
}

NotificationView.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    category: PropTypes.oneOf(Object.values(NotificationType)).isRequired,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      .isRequired
  }).isRequired,
  onRemove: PropTypes.func.isRequired
}

export default NotificationView
